import { useState } from 'react';
import { sanitizeHtml } from '@/lib/sanitize-html';
import { useQuery, useMutation } from '@tanstack/react-query';
import { queryClient, apiRequest } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { Mail, FolderSearch, Forward, X, ChevronDown, ChevronRight, AlertCircle, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

interface EmailNotificationAdmin {
  id: number;
  userId: number;
  emailAccountId: string;
  emailUID: number;
  suggestedProjectId: string | null;
  detectionMethod: string | null;
  isActioned: boolean;
  flagReason: string;
  createdAt: string;
  emailData: {
    subject: string;
    fromAddress: string;
    fromName: string | null;
    toAddresses: string[];
    ccAddresses: string[];
    emailBody: string;
    emailDate: string;
  };
  project: {
    id: string;
    projectName: string;
  } | null;
}

const FLAG_REASON_LABELS: Record<string, { label: string; color: string }> = {
  nonexistent_project: { label: 'Non-existent Project', color: 'bg-orange-600' },
  blacklist_admin: { label: 'Blacklist (Admin)', color: 'bg-red-600' },
  blacklist_chatgpt: { label: 'Blacklist (AI)', color: 'bg-purple-600' },
  chatgpt_rejected: { label: 'Spam/Irrelevant (AI)', color: 'bg-yellow-600' },
};

export function EmailAdminReview() {
  const { toast } = useToast();
  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  const [expandedEmail, setExpandedEmail] = useState<number | null>(null);
  const [selectedEmails, setSelectedEmails] = useState<Set<number>>(new Set());
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [selectedEmailId, setSelectedEmailId] = useState<number | null>(null);
  const [projectSearchQuery, setProjectSearchQuery] = useState('');

  // Fetch flagged emails with optional filter
  const { data: flaggedEmails = [], isLoading } = useQuery<EmailNotificationAdmin[]>({
    queryKey: ['/api/email-notifications/admin-review', selectedFilter],
    queryFn: async () => {
      const url = selectedFilter === 'all' 
        ? '/api/email-notifications/admin-review'
        : `/api/email-notifications/admin-review?flagReason=${selectedFilter}`;
      const response = await apiRequest('GET', url);
      if (!response.ok) {
        throw new Error('Failed to fetch flagged emails');
      }
      return response.json();
    },
  });

  // Fetch all projects for reassignment
  const { data: allProjects = [] } = useQuery<Array<{ id: string; projectName: string }>>({
    queryKey: ['/api/projects'],
    enabled: reassignDialogOpen,
  });

  // Get counts by flag reason
  const flagCounts = flaggedEmails.reduce((acc, email) => {
    acc[email.flagReason] = (acc[email.flagReason] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Dismiss/ignore mutation
  const dismissMutation = useMutation({
    mutationFn: async (emailIds: number[]) => {
      const results = await Promise.all(
        emailIds.map(id => apiRequest('POST', `/api/email-notifications/${id}/dismiss`, {}))
      );
      const failures = results.filter(r => !r.ok);
      if (failures.length > 0) {
        throw new Error(`Failed to dismiss ${failures.length} email(s)`);
      }
    },
    onSuccess: (_, emailIds) => {
      queryClient.invalidateQueries({ queryKey: ['/api/email-notifications/admin-review'] });
      setSelectedEmails(new Set());
      toast({
        title: 'Success',
        description: `Dismissed ${emailIds.length} email(s)`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Forward mutation
  const forwardMutation = useMutation({
    mutationFn: async (emailIds: number[]) => {
      const results = await Promise.all(
        emailIds.map(id => apiRequest('POST', `/api/email-notifications/${id}/forward`, {}))
      );
      const failures = results.filter(r => !r.ok);
      if (failures.length > 0) {
        throw new Error(`Failed to forward ${failures.length} email(s)`);
      }
    },
    onSuccess: (_, emailIds) => {
      queryClient.invalidateQueries({ queryKey: ['/api/email-notifications/admin-review'] });
      setSelectedEmails(new Set());
      toast({
        title: 'Success',
        description: `Forwarded ${emailIds.length} email(s)`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Reassign mutation
  const reassignMutation = useMutation({
    mutationFn: async ({ emailId, projectId }: { emailId: number; projectId: string }) => {
      const response = await apiRequest('POST', `/api/email-notifications/${emailId}/reassign`, { projectId });
      if (!response.ok) {
        throw new Error('Failed to reassign email');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/email-notifications/admin-review'] });
      // Reassign creates a pulse in the target project server-side — refresh
      // the tree directly instead of relying on the WS echo (no replay).
      queryClient.invalidateQueries({ queryKey: ['/api/tree/snapshot'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['/api/projects'] });
      queryClient.invalidateQueries({ queryKey: ['/api/years'] });
      setReassignDialogOpen(false);
      setSelectedEmailId(null);
      setProjectSearchQuery('');
      toast({
        title: 'Success',
        description: 'Email reassigned successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleToggleEmail = (emailId: number) => {
    const newSelected = new Set(selectedEmails);
    if (newSelected.has(emailId)) {
      newSelected.delete(emailId);
    } else {
      newSelected.add(emailId);
    }
    setSelectedEmails(newSelected);
  };

  const handleToggleAll = () => {
    if (selectedEmails.size === flaggedEmails.length) {
      setSelectedEmails(new Set());
    } else {
      setSelectedEmails(new Set(flaggedEmails.map(e => e.id)));
    }
  };

  const handleDismiss = (emailId: number) => {
    dismissMutation.mutate([emailId]);
  };

  const handleBulkDismiss = () => {
    dismissMutation.mutate(Array.from(selectedEmails));
  };

  const handleForward = (emailId: number) => {
    forwardMutation.mutate([emailId]);
  };

  const handleBulkForward = () => {
    forwardMutation.mutate(Array.from(selectedEmails));
  };

  const handleOpenReassign = (emailId: number) => {
    setSelectedEmailId(emailId);
    setReassignDialogOpen(true);
  };

  const handleReassignSubmit = (projectId: string) => {
    if (selectedEmailId) {
      reassignMutation.mutate({ emailId: selectedEmailId, projectId });
    }
  };

  const filteredProjects = allProjects.filter(project =>
    project.id.toLowerCase().includes(projectSearchQuery.toLowerCase()) ||
    project.projectName.toLowerCase().includes(projectSearchQuery.toLowerCase())
  );

  const getFlagBadgeColor = (flagReason: string) => {
    return FLAG_REASON_LABELS[flagReason]?.color || 'bg-gray-600';
  };

  const getFlagLabel = (flagReason: string) => {
    return FLAG_REASON_LABELS[flagReason]?.label || flagReason;
  };

  const getDetectionBadge = (method: string | null) => {
    if (!method) return null;
    const color = method === 'thread' ? 'bg-blue-600' : 'bg-green-600';
    return <Badge className={cn(color, 'text-white text-xs')}>{method}</Badge>;
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-white">Email Admin Review</h3>
        <p className="text-sm text-gray-400 mt-1">
          Review and manage flagged email notifications
        </p>
      </div>

      {/* Filter Tabs */}
      <Tabs value={selectedFilter} onValueChange={setSelectedFilter}>
        <TabsList className="bg-gray-700">
          <TabsTrigger value="all" className="data-[state=active]:bg-gray-600" data-testid="filter-all">
            All
            {flaggedEmails.length > 0 && (
              <Badge className="ml-2 bg-blue-600 text-white">{flaggedEmails.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="nonexistent_project" className="data-[state=active]:bg-gray-600" data-testid="filter-nonexistent">
            Non-existent
            {flagCounts.nonexistent_project > 0 && (
              <Badge className="ml-2 bg-orange-600 text-white">{flagCounts.nonexistent_project}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="blacklist_admin" className="data-[state=active]:bg-gray-600" data-testid="filter-blacklist-admin">
            Blacklist (Admin)
            {flagCounts.blacklist_admin > 0 && (
              <Badge className="ml-2 bg-red-600 text-white">{flagCounts.blacklist_admin}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="blacklist_chatgpt" className="data-[state=active]:bg-gray-600" data-testid="filter-blacklist-ai">
            Blacklist (AI)
            {flagCounts.blacklist_chatgpt > 0 && (
              <Badge className="ml-2 bg-purple-600 text-white">{flagCounts.blacklist_chatgpt}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="chatgpt_rejected" className="data-[state=active]:bg-gray-600" data-testid="filter-spam">
            Spam/Irrelevant
            {flagCounts.chatgpt_rejected > 0 && (
              <Badge className="ml-2 bg-yellow-600 text-white">{flagCounts.chatgpt_rejected}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value={selectedFilter} className="mt-4">
          {/* Bulk Actions */}
          {selectedEmails.size > 0 && (
            <div className="flex items-center gap-2 mb-4 p-3 bg-gray-700 rounded-lg border border-gray-600">
              <span className="text-white text-sm">
                {selectedEmails.size} selected
              </span>
              <Button
                onClick={handleBulkDismiss}
                variant="outline"
                size="sm"
                className="ml-auto"
                disabled={dismissMutation.isPending}
                data-testid="button-bulk-dismiss"
              >
                <X className="w-4 h-4 mr-2" />
                Dismiss All
              </Button>
              <Button
                onClick={handleBulkForward}
                variant="outline"
                size="sm"
                disabled={forwardMutation.isPending}
                data-testid="button-bulk-forward"
              >
                <Forward className="w-4 h-4 mr-2" />
                Forward All
              </Button>
            </div>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="text-center py-8 text-gray-400">
              Loading flagged emails...
            </div>
          )}

          {/* Empty State */}
          {!isLoading && flaggedEmails.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No flagged emails found</p>
            </div>
          )}

          {/* Email Table */}
          {!isLoading && flaggedEmails.length > 0 && (
            <div className="border border-gray-600 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-700 hover:bg-gray-700">
                    <TableHead className="w-12">
                      <Checkbox
                        checked={selectedEmails.size === flaggedEmails.length}
                        onCheckedChange={handleToggleAll}
                        data-testid="checkbox-select-all"
                      />
                    </TableHead>
                    <TableHead className="text-white">Subject</TableHead>
                    <TableHead className="text-white">From</TableHead>
                    <TableHead className="text-white">Date</TableHead>
                    <TableHead className="text-white">Suggested Project</TableHead>
                    <TableHead className="text-white">Detection</TableHead>
                    <TableHead className="text-white">Flag Reason</TableHead>
                    <TableHead className="text-white">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {flaggedEmails.map((email) => (
                    <>
                      <TableRow
                        key={email.id}
                        className="hover:bg-gray-700 cursor-pointer"
                        onClick={() => setExpandedEmail(expandedEmail === email.id ? null : email.id)}
                        data-testid={`row-email-${email.id}`}
                      >
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedEmails.has(email.id)}
                            onCheckedChange={() => handleToggleEmail(email.id)}
                            data-testid={`checkbox-email-${email.id}`}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {expandedEmail === email.id ? (
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-400" />
                            )}
                            <span className="font-semibold text-white" data-testid={`text-subject-${email.id}`}>
                              {email.emailData.subject}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-gray-300">
                            {email.emailData.fromName && (
                              <div className="font-medium">{email.emailData.fromName}</div>
                            )}
                            <div className="text-gray-400">{email.emailData.fromAddress}</div>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-gray-300">
                          {format(new Date(email.emailData.emailDate), 'MMM d, yyyy HH:mm')}
                        </TableCell>
                        <TableCell>
                          {email.suggestedProjectId ? (
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-300">{email.suggestedProjectId}</span>
                              {!email.project && (
                                <div className="relative group">
                                  <AlertCircle className="w-4 h-4 text-orange-500" />
                                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                                    Project does not exist
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-500">None</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {getDetectionBadge(email.detectionMethod)}
                        </TableCell>
                        <TableCell>
                          <Badge className={cn(getFlagBadgeColor(email.flagReason), 'text-white')}>
                            {getFlagLabel(email.flagReason)}
                          </Badge>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-2">
                            <Button
                              onClick={() => handleOpenReassign(email.id)}
                              variant="outline"
                              size="sm"
                              className="text-xs"
                              data-testid={`button-assign-${email.id}`}
                            >
                              <FolderSearch className="w-3 h-3 mr-1" />
                              Assign
                            </Button>
                            <Button
                              onClick={() => handleForward(email.id)}
                              variant="outline"
                              size="sm"
                              className="text-xs"
                              disabled={forwardMutation.isPending}
                              data-testid={`button-forward-${email.id}`}
                            >
                              <Forward className="w-3 h-3" />
                            </Button>
                            <Button
                              onClick={() => handleDismiss(email.id)}
                              variant="outline"
                              size="sm"
                              className="text-xs text-red-400 hover:text-red-300"
                              disabled={dismissMutation.isPending}
                              data-testid={`button-dismiss-${email.id}`}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      {/* Expanded Email Content */}
                      {expandedEmail === email.id && (
                        <TableRow className="bg-gray-800">
                          <TableCell colSpan={8} className="p-6">
                            <div className="space-y-4">
                              {/* CC Addresses */}
                              {email.emailData.ccAddresses.length > 0 && (
                                <div>
                                  <span className="text-sm font-semibold text-gray-400">CC: </span>
                                  <span className="text-sm text-gray-300">
                                    {email.emailData.ccAddresses.join(', ')}
                                  </span>
                                </div>
                              )}
                              
                              {/* To Addresses */}
                              {email.emailData.toAddresses.length > 0 && (
                                <div>
                                  <span className="text-sm font-semibold text-gray-400">To: </span>
                                  <span className="text-sm text-gray-300">
                                    {email.emailData.toAddresses.join(', ')}
                                  </span>
                                </div>
                              )}
                              
                              {/* Email Body */}
                              <div>
                                <div className="text-sm font-semibold text-gray-400 mb-2">Email Body:</div>
                                <div 
                                  className="text-sm text-gray-300 bg-gray-900 p-4 rounded border border-gray-700 max-h-96 overflow-y-auto"
                                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(email.emailData.emailBody) }}
                                  data-testid={`text-email-body-${email.id}`}
                                />
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Reassign Dialog */}
      <Dialog open={reassignDialogOpen} onOpenChange={setReassignDialogOpen}>
        <DialogContent className="max-w-2xl navy-card border-gray-600">
          <DialogHeader>
            <DialogTitle className="text-white">Assign Email to Project</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Input
                placeholder="Search projects by ID or name..."
                value={projectSearchQuery}
                onChange={(e) => setProjectSearchQuery(e.target.value)}
                className="bg-gray-700 border-gray-600 text-white"
                data-testid="input-project-search"
              />
            </div>
            
            <div className="max-h-96 overflow-y-auto border border-gray-600 rounded-lg">
              {filteredProjects.length === 0 ? (
                <div className="p-4 text-center text-gray-400">
                  No projects found
                </div>
              ) : (
                <div className="divide-y divide-gray-600">
                  {filteredProjects.map((project) => (
                    <button
                      key={project.id}
                      onClick={() => handleReassignSubmit(project.id)}
                      className="w-full p-3 text-left hover:bg-gray-700 transition-colors"
                      disabled={reassignMutation.isPending}
                      data-testid={`button-project-${project.id}`}
                    >
                      <div className="font-medium text-white">{project.id}</div>
                      <div className="text-sm text-gray-400">{project.projectName}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button
              onClick={() => {
                setReassignDialogOpen(false);
                setProjectSearchQuery('');
              }}
              variant="outline"
              data-testid="button-cancel-reassign"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
